<template>
  <div class="seal-test-container">
    <van-nav-bar title="印章相似度测试" left-arrow @click-left="$router.back()" />
    
    <div class="content">
      <!-- 规则选择 -->
      <div class="form-group">
        <label>选择规则</label>
        <el-select v-model="selectedRuleId" placeholder="请选择印章规则" @change="onRuleChange">
          <el-option
            v-for="rule in allRules"
            :key="rule.ruleCode"
            :label="rule.remark"
            :value="rule.ruleCode"
          />
        </el-select>
      </div>

      <!-- 调用门店编码（持久化到本地，刷新不丢失） -->
      <div class="form-group">
        <label>门店编码</label>
        <el-input
          v-model="authStoreCode"
          placeholder="请输入门店编码"
          clearable
          @input="saveAuthStoreCodeToCache"
        />
      </div>

      <!-- 印章绘制区域 -->
      <div class="canvas-container">
        <canvas
          ref="canvas"
          width="400"
          height="400"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd"
        ></canvas>
        <p class="hint">触摸屏幕记录触点位置（4个触点将进行相似度对比）</p>
      </div>

      <!-- 点位显示 -->
      <div v-if="touchPoints.length > 0" class="points-info">
        <p><strong>已绘制点位 ({{ touchPoints.length }}):</strong></p>
        <p v-for="(point, idx) in touchPoints" :key="idx">
          点{{ idx + 1 }}: [{{ point[0] }}, {{ point[1] }}]
        </p>
      </div>

      <!-- 操作按钮 -->
      <div class="button-group">
        <van-button type="primary" @click="compareSeals" :disabled="touchPoints.length === 0 || !currentRule">
          {{ touchPoints.length === 4 ? '对比相似度' : '记录数据' }}
        </van-button>
        <van-button @click="clearCanvas">清空画布</van-button>
      </div>

      <!-- 对比结果弹窗 -->
      <el-dialog
        title="对比结果"
        :visible.sync="showResultDialog"
        width="90%"
        @close="handleDialogClose"
      >
        <div v-if="comparisonResult" class="result-dialog-content">
          <div class="result-item" v-if="comparisonResult.currentPoint !== undefined">
            <span class="label">当前得分:</span>
            <span class="value">{{ comparisonResult.currentPoint }}分</span>
          </div>
          <div class="result-item" v-if="comparisonResult.templatePoints">
            <span class="label">标准点位:</span>
            <span class="value">{{ JSON.stringify(comparisonResult.templatePoints) }}</span>
          </div>
          <div class="result-item" v-if="comparisonResult.currentPoints">
            <span class="label">当前点位:</span>
            <span class="value">{{ JSON.stringify(comparisonResult.currentPoints) }}</span>
          </div>
          <div v-if="comparisonResult.valid !== undefined || comparisonResult.matched !== undefined" class="result-item">
            <span class="label">匹配结果:</span>
            <span :class="(comparisonResult.valid || comparisonResult.matched) ? 'matched' : 'not-matched'">
              {{ (comparisonResult.valid || comparisonResult.matched) ? '✓ 匹配成功' : '✗ 匹配失败' }}
            </span>
          </div>
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showResultDialog = false">关闭</el-button>
        </span>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { getSealRules, compareSealPoints } from '@/api/sealTest'

export default {
  name: 'SealTest',
  data() {
    return {
      ruleList: [],
      allRules: [],
      selectedRuleId: null,
      currentRule: null,
      showRuleList: false,
      /** 调用门店编码，从 localStorage 读取，输入时写回缓存 */
      authStoreCode: '',
      touchPoints: [],
      isDrawing: false,
      comparisonResult: null,
      showResultDialog: false,
      canvas: null,
      ctx: null,
      // 标准坐标系（用于跨设备兼容，与录入页面保持一致）
      standardWidth: 800,
      standardHeight: 600,
      // 防抖定时器
      debounceTimer: null
    }
  },
  mounted() {
    this.canvas = this.$refs.canvas
    this.ctx = this.canvas.getContext('2d')
    this.authStoreCode = (typeof localStorage !== 'undefined' && localStorage.getItem('sealTest_authStoreCode')) || ''
    this.loadRules()
  },
  beforeDestroy() {
    // 组件销毁时清除定时器，避免内存泄漏
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  },
  methods: {
    async loadRules() {
      try {
        const res = await getSealRules()
        const rules = res.data || res
        this.ruleList = rules.map(rule => ({
          text: rule.ruleCode,
          value: rule.id
        }))
        this.allRules = rules
      } catch (err) {
        console.error('加载规则失败:', err)
        this.$toast.fail('加载规则失败')
      }
    },
    onRuleChange(ruleCode) {
      console.log('规则变更:', ruleCode, 'allRules:', this.allRules)
      this.currentRule = this.allRules.find(r => r.ruleCode === ruleCode)
      console.log('选中规则:', this.currentRule)
    },
    /** 将 authStoreCode 写入 localStorage，刷新页面不丢失 */
    saveAuthStoreCodeToCache() {
      if (typeof localStorage === 'undefined') return
      const val = (this.authStoreCode && this.authStoreCode.trim()) || ''
      if (val) {
        localStorage.setItem('sealTest_authStoreCode', val)
      } else {
        localStorage.removeItem('sealTest_authStoreCode')
      }
    },
    handleTouchStart(e) {
      e.preventDefault()
      // 支持任意数量的触点
      if (e.touches.length > 0) {
        // 清空之前的画布和点位数据
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
        this.touchPoints = []
        
        const rect = this.canvas.getBoundingClientRect()
        // 计算缩放比例（canvas 实际尺寸 vs 显示尺寸）
        const scaleX = this.canvas.width / rect.width
        const scaleY = this.canvas.height / rect.height

        // 收集所有触点的坐标并转换为标准800x600像素坐标
        for (let i = 0; i < e.touches.length; i++) {
          const touch = e.touches[i]
          const x = touch.clientX - rect.left
          const y = touch.clientY - rect.top
          
          // 转换为 canvas 内部坐标（用于绘制）
          const canvasX = x * scaleX
          const canvasY = y * scaleY
          
          // 转换为标准800x600像素坐标（用于对比，保证跨设备兼容）
          const standardX = Math.round((x / rect.width) * this.standardWidth)
          const standardY = Math.round((y / rect.height) * this.standardHeight)
          
          this.touchPoints.push([standardX, standardY])
          this.drawPoint(canvasX, canvasY, i + 1)
        }

        console.log(`${e.touches.length}个触点，点位记录（标准坐标）:`, this.touchPoints)

        // 防抖处理：清除之前的定时器，1秒内只触发一次接口调用
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
        }
        
        // 设置新的定时器，1秒后调用接口
        this.debounceTimer = setTimeout(() => {
          this.compareSeals()
          this.debounceTimer = null
        }, 1000)
      }
    },
    handleTouchEnd(e) {
      // 手指离开时，如果还有定时器，立即执行接口调用（不再等待）
      if (e.touches.length === 0) {
        console.log('所有手指离开')
        // 如果还有待执行的定时器，立即执行并清除
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer)
          this.debounceTimer = null
          // 如果有点位数据，立即调用接口
          if (this.touchPoints.length > 0) {
            this.compareSeals()
          }
        }
      }
    },
    drawPoint(x, y, index) {
      // 绘制红色圆点
      this.ctx.fillStyle = '#FF0000'
      this.ctx.beginPath()
      this.ctx.arc(x, y, 8, 0, 2 * Math.PI)
      this.ctx.fill()
      
      // 绘制序号文字
      this.ctx.fillStyle = '#FFFFFF'
      this.ctx.font = 'bold 12px Arial'
      this.ctx.textAlign = 'center'
      this.ctx.textBaseline = 'middle'
      this.ctx.fillText(index.toString(), x, y)
    },
    clearCanvas() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.touchPoints = []
      this.comparisonResult = null
    },
    async compareSeals() {
      console.log(this.touchPoints, this.currentRule)
      if (this.touchPoints.length === 0 || !this.currentRule) {
        this.$toast.fail('请选择规则并触摸屏幕')
        return
      }

      try {
        const res = await compareSealPoints({
          ruleCode: this.currentRule.ruleCode,
          touchPoints: this.touchPoints,
          authStoreCode: this.authStoreCode
        })
        console.log('接口结果:', res)
        // 处理响应数据（新接口返回格式：{ status: 200, data: { valid, similarity, threshold, standardPoint, currentPoint, message } }）
        if (res && res.data) {
          this.comparisonResult = res.data
        } else if (res && (res.valid !== undefined || res.matched !== undefined)) {
          // 兼容旧格式或直接返回结果的情况
          this.comparisonResult = res
        } else {
          this.comparisonResult = res
        }
        console.log('最终结果:', this.comparisonResult)
        
        // 如果是4个触点，显示对比结果弹窗；否则只提示记录成功
        if (this.touchPoints.length === 4) {
          this.showResultDialog = true
        } else {
          this.$toast.success(`已记录 ${this.touchPoints.length} 个触点数据`)
        }
      } catch (err) {
        console.error('接口调用失败:', err)
        this.$toast.fail('操作失败: ' + (err.message || ''))
      }
    },
    handleDialogClose() {
      this.showResultDialog = false
      this.clearCanvas()
    }
  }
}
</script>

<style scoped>
.seal-test-container {
  background: #f5f5f5;
  min-height: 100vh;
}

.content {
  padding: 16px;
}

.form-group {
  background: white;
  padding: 16px;
  margin: 12px 0;
  border-radius: 4px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
}

.form-group .el-select {
  width: 100%;
}

.rule-info, .points-info, .result-info {
  background: white;
  padding: 12px;
  margin: 12px 0;
  border-radius: 4px;
  font-size: 14px;
}

.canvas-container {
  background: white;
  margin: 12px 0;
  border-radius: 4px;
  text-align: center;
  padding: 12px;
}

canvas {
  border: 1px solid #ddd;
  cursor: crosshair;
  max-width: 100%;
}

.hint {
  color: #999;
  font-size: 12px;
  margin-top: 8px;
}

.button-group {
  display: flex;
  gap: 8px;
  margin: 16px 0;
}

.button-group button {
  flex: 1;
}

.matched {
  color: #07c160;
}

.not-matched {
  color: #ff0000;
}
</style>

